import { gpx, kml } from "@tmcw/togeojson"
import { get } from "nodash"
import { Geometry } from "geojson"
import { DOMParser } from "@xmldom/xmldom"
import JSZip from "jszip"
import { VideoMetadata } from "@nova/slice-video"
import { parseTelemetryFile } from "plugins/Video/FMV/telemetry"

export const getPointsFromFile = async (file: File): Promise<[number, number, number][][]> => {
    const extension = file.name.split(".").pop()?.toLowerCase()
    if (extension === "kml") {
        const content = await parseKMLFile(file)
        return getGeojsonFromKmlString(content)
    } else if (extension === "kmz") {
        const content = await parseKMZFile(file)
        return (await Promise.all(content.map((d) => getGeojsonFromKmlString(d)))).flat()
    } else if (extension === "gpx") {
        const content = await parseGPXFile(file)
        return getGeojsonFromGpxString(content)
    } else if (extension === "srt") {
        const content = await parseTelemetryFile(file)
        if (!content) throw ".SRT contains invalid metadata."
        return getGeojsonFromSRTMetadata(content)
    } else {
        throw "invalid file type provided to validateGeometryFileUpload"
    }
}

//KML or KMZ files
export const validatePolygonFileUpload = async (files: File[]) => {
    if (files.length > 1) console.warn("Multiple files uploaded when there should only be one")
    const file = files[0]
    try {
        const extension = file.name.split(".").pop()?.toLowerCase()
        if (extension === "kml") {
            const content = await parseKMLFile(file)
            const polygons = getGeojsonFromKmlString(content)
            if (!polygons || polygons.length === 0)
                throw new Error("Could not find valid polygons in kml")
            if (!polygons.every((polygon) => polygon?.length > 2))
                throw new Error("KML contains invalid polygons")
        } else if (extension === "kmz") {
            const content = await parseKMZFile(file)
            const hasAnyValidKMLs = content.some((c) => {
                const polygons = getGeojsonFromKmlString(c)
                const hasValidKMLs =
                    polygons?.length > 0 && polygons.every((polygon) => polygon.length > 2)
                return hasValidKMLs
            })
            if (!hasAnyValidKMLs) throw new Error("KMZ contains no valid KMLs")
        } else if (extension === "gpx") {
            const content = await parseGPXFile(file)
            const polygons = getGeojsonFromGpxString(content)
            if (!polygons || polygons.length === 0)
                throw new Error("Could not find valid polygons in gpx")
            if (!polygons.every((polygon) => polygon?.length > 2))
                throw new Error("GPX contains invalid polygons")
        } else if (extension === "srt") {
            const content = await parseTelemetryFile(file)
            if (!content) throw ".SRT contains invalid metadata."
            const polygons = getGeojsonFromSRTMetadata(content)
            if (!polygons || polygons.length === 0)
                throw new Error("Could not find valid polygons in SRT")
            if (polygons.some((polygon) => polygon?.length < 3))
                throw new Error("SRT contains invalid polygons")
        } else {
            throw "invalid file type provided to validatePolygonFileUpload"
        }
        return [true, ""]
    } catch (err) {
        console.error(err)
        return [false, get(err, "message") || ""]
    }
}

export const parseKMLFile = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = () => {
            try {
                const content = reader.result as string
                resolve(content)
            } catch (error) {
                reject(error)
            }
        }
        reader.onerror = () => {
            reject(reader.error)
        }
        reader.readAsText(file)
    })
}

export const parseGPXFile = parseKMLFile

export const parseKMZFile = async (file: File): Promise<string[]> => {
    try {
        const arrayBuffer = await file.arrayBuffer()
        const zip = await JSZip.loadAsync(arrayBuffer)

        const kmlFiles: string[] = []
        for (const filename in zip.files) {
            if (filename.toLowerCase().endsWith(".kml")) {
                try {
                    const content = await zip.files[filename].async("string")
                    kmlFiles.push(content)
                } catch (err) {
                    console.warn(`Failed to read ${filename}:`, err)
                }
            }
        }
        return kmlFiles
    } catch (error) {
        throw error
    }
}

export const geometryParsers: Record<
    string,
    (item: Geometry | null) => [number, number, number][][]
> = {
    LineString: (item) => [get(item, "coordinates") || []] as [number, number, number][][],
    Polygon: (item) => (get(item, "coordinates") || []) as [number, number, number][][],
    GeometryCollection: (item) =>
        get(item, "geometries", [] as Geometry[]).reduce(
            (acc, item) => acc.concat(geometryParsers[item.type || ""]?.(item) || []),
            [] as [number, number, number][][]
        ),
}

/**
 * Parses kml to [longitude, latitude, altitude][i][j]
   where i is the polygon index,
   and j is the coordinate index,
   forcing the altitude to zero to display flatly on the map.
 */
export const getGeojsonFromKmlString = (kmlString: string) => {
    const content = kml(new DOMParser().parseFromString(kmlString, "text/xml"))
    return content.features.reduce(
        (acc, item) =>
            acc.concat(
                (
                    geometryParsers[item.geometry?.type || ""]?.(item.geometry) as [
                        number,
                        number,
                        number,
                    ][][]
                )?.map((polygon) =>
                    polygon.map((a) => [a[0], a[1], 0] as [number, number, number])
                ) || []
            ),
        [] as [number, number, number][][]
    )
}

export const getGeojsonFromGpxString = (gpxString: string) => {
    const content = gpx(new DOMParser().parseFromString(gpxString, "text/gpx"))
    return content.features.reduce(
        (acc, item) =>
            acc.concat(
                (
                    geometryParsers[item.geometry?.type || ""]?.(item.geometry) as [
                        number,
                        number,
                        number,
                    ][][]
                )?.map((polygon) =>
                    polygon.map((a) => [a[0], a[1], 0] as [number, number, number])
                ) || []
            ),
        [] as [number, number, number][][]
    )
}

const getGeojsonFromSRTMetadata = (metadata: VideoMetadata): [number, number, number][][] => {
    return [metadata.map((frame) => frame.slice(0, 3) as [number, number, number])]
}
